'use client'

import * as React from 'react'
import { FeedbackCommentEditor } from './components/editor'
import {
  Popover,
  PopoverContent,
  PopoverPortal,
  PopoverTrigger,
} from '@radix-ui/react-popover'
import { IconButton } from '~/sotano-kit/primitives/icon-button'
import { CommentIcon } from './assets/comment-icon'
import {
  Block,
  BlockChange,
  Comment,
  RootComment,
  isBlockWithChildren,
} from '@basehub/data/replicache/index'
import { BaseHubAvatar } from '~/components/basehub-avatar'
import { useUserStore } from '~/app/_store/user-store'
import {
  CheckCircledIcon,
  CheckIcon,
  Cross1Icon,
  PlusIcon,
} from '@radix-ui/react-icons'
import { makeFullNameFromClerkUser } from '@basehub/data/auth/utils/make-full-name-from-clerk-user'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import {
  CommentForm,
  CommentFormFooter,
  CommentReactions,
  CommentThreadBox,
  CommentWrapper,
  ThreadActions,
  safeParseCommentValue,
} from './components'
import { clsx } from 'clsx'
import { useUserData } from '~/hooks-data/use-user-data'
import {
  useSelectedBranch,
  useSelectedBranchOrCommitQuery,
} from '~/lib/commits/use-selected-branch-or-commit'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { Editor, JSONContent } from '@tiptap/core'
import { useUserPreferences } from '~/hooks-data/use-user-preferences'
import { tabContentWrapperId } from '../tab-content/wrapper'
import { getId } from '@basehub/utils/id/index'
import { ScrollArea } from '~/sotano-kit/primitives/scroll-area'
import { useDashboardSlugs } from '~/hooks/use-dashboard-slugs'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import { useNotifications } from '~/app/(dash)/[team-slug]/header/notifications/hooks/use-notifications'
import { useTeamReplicache } from '~/hooks-data/use-team-replicache'
import { useCommentsStore } from './provider'
import { invariant } from '@epic-web/invariant'
import { Replicache } from 'replicache'
import { repoSpaceMutators } from '@basehub/data/replicache/mutators/repo-space/mutators'
import { useHasRendered } from '~/hooks/use-has-rendered'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '~/sotano-kit/primitives/select'
import { useSearchParams } from 'next/navigation'
import useWebSocketConnection from '~/hooks/use-websocket-connection'

/* -------------------------------------------------------------------------- */
/*                                    Root                                    */
/* -------------------------------------------------------------------------- */

export const useThread = (
  root: RootComment | null | undefined
): [RootComment, ...Comment[]] | undefined => {
  const commentsMap = useCommentsStore((s) => s.commentsMap)

  return React.useMemo(() => {
    if (!root) return undefined

    const thread: [RootComment, ...Comment[]] = [root]

    function processReply(id: string) {
      const comment = commentsMap.get(id)
      if (!comment) return
      thread.push(comment)
      if (comment.replyId) {
        processReply(comment.replyId)
      }
    }

    if (root.replyId) {
      processReply(root.replyId)
    }

    return thread
  }, [root, commentsMap])
}

export const useLastCommentGetter = (): ((
  root: RootComment | null | undefined
) => Comment | undefined) => {
  const commentsMap = useCommentsStore((s) => s.commentsMap)

  return React.useCallback(
    (root: RootComment | null | undefined): Comment | undefined => {
      if (!root) return undefined

      let current: Comment = root
      while (current.replyId) {
        const next = commentsMap.get(current.replyId)
        if (!next) break
        current = next
      }

      return current
    },
    [commentsMap]
  )
}

export const CommentsPopover = ({
  block,
  isVisible = true,
  className,
  type,
  side = 'left',
  align = 'start',
}: {
  block: Block | BlockChange
  isVisible?: boolean
  className?: string
  type: 'block' | 'diff'
  side?: 'left' | 'right' | 'top' | 'bottom'
  align?: 'start' | 'end'
}) => {
  const commentTriggerRef = React.useRef<HTMLButtonElement | null>(null)
  const user = useUserStore((s) => s.optimisticUser)
  const { branch } = useSelectedBranch()
  const replicache = useRepoReplicache().data?.replicache
  const teamReplicache = useTeamReplicache().data
  const sourceBlockId = idToSourceBlockId(block)
  const searchParams = useSearchParams()
  // Interactions
  const [isHovered, setIsHovered] = React.useState(false)
  const [isOpen, setIsOpen] = React.useState(false)
  const [threadPickerIsOpen, setThreadPickerIsOpen] = React.useState(false)
  const ref = React.useRef<HTMLDivElement | null>(null)
  const contentRef = React.useRef<HTMLDivElement | null>(null)

  const closeTimerRef = React.useRef(0)
  const isPointerDownOnContentRef = React.useRef(false)
  const hasSelectionRef = React.useRef(false)

  const closeDelay = 100

  const selectedRootId = useUserPreferences((s) => {
    const threadsOfThisBlock = s.selectedThreads[sourceBlockId]

    if (!threadsOfThisBlock?.length) return null

    return (
      threadsOfThisBlock.find((root) => {
        return root.blockId === sourceBlockId && root.branchId === branch?.id
      })?.id ?? null
    )
  })

  const threadRoots = useCommentsStore((s) => {
    const roots = s.rootsByBlockId.get(sourceBlockId)
    const threadRoots: Array<RootComment> = []

    for (const rId of roots ?? []) {
      const comment = s.commentsMap.get(rId)
      if (!comment) continue
      if (
        comment.type === 'root' &&
        comment.blockId === sourceBlockId &&
        comment.branchId === branch?.id
      ) {
        threadRoots.push(comment)
      }
    }

    return threadRoots
  })

  const selectedRoot = useCommentsStore((s) => {
    const roots = s.rootsByBlockId.get(sourceBlockId)
    const selectedRoot = roots?.has(selectedRootId ?? '')
      ? s.commentsMap.get(selectedRootId!)
      : null
    invariant(
      !selectedRoot || selectedRoot.type === 'root',
      'Expected comment to be root'
    )
    return (selectedRoot as RootComment) ?? null
  })
  const roots = useCommentsStore((s) => {
    const rootIdArray = s.rootsByBlockId.get(sourceBlockId)
    const _roots = new Set<RootComment>()
    rootIdArray?.forEach((id) => {
      const comment = s.commentsMap.get(id)
      if (comment && comment.type === 'root') {
        _roots.add(comment as RootComment)
      }
    })
    return _roots
  })
  const thread = useThread(selectedRoot)
  // get notifications
  const { notifications } = useNotifications()

  const notificationsRelatedToThread = React.useMemo(() => {
    if (!selectedRoot) return []
    return notifications?.filter(
      (n) =>
        n.type === 'repo-thread-comment' &&
        selectedRoot.id === n.rootId &&
        n.read === false
    )
  }, [selectedRoot, notifications])

  const getLastComment = useLastCommentGetter()
  const lastUnreadThread = useLastUnreadThread(
    Array.from(roots)
      .map((rootComment) => {
        const lastComment = getLastComment(rootComment)
        if (!lastComment) return null

        return { rootComment, lastComment }
      })
      .filter(Boolean),
    { selectedRootId: selectedRoot?.id }
  )

  // Check if the comment is open
  const shouldShow =
    isVisible ||
    !!thread?.length ||
    isOpen ||
    threadPickerIsOpen ||
    !!lastUnreadThread
  const isOpenResolved =
    (isHovered && (thread?.length ?? 0) > 0) || isOpen || threadPickerIsOpen
  // Read the comment
  const readComment = React.useCallback(async () => {
    if (!user || !selectedRoot || !replicache) return
    const promises =
      notificationsRelatedToThread?.map((n) => {
        return teamReplicache?.replicache.mutate.markNotificationRead(n.id)
      }) ?? []
    await Promise.all([
      ...promises,
      replicache.mutate.setCommentRead({
        userId: user.id,
        commentId: selectedRoot?.id,
        isoNow: new Date().toISOString(),
      }),
    ])
  }, [
    replicache,
    user,
    selectedRoot,
    notificationsRelatedToThread,
    teamReplicache,
  ])

  // Clear search params ----------------------------------------------
  const clearSearchParams = React.useCallback(() => {
    const searchParams = new URLSearchParams(window.location.search)
    searchParams.delete('commentId')
    window.history.replaceState(
      {},
      '',
      `${window.location.pathname}?${searchParams}`
    )
  }, [])

  // Handle the interaction state -------------------------------------
  React.useEffect(() => {
    // If the comment is not loaded, don't read it
    if (!selectedRoot) return
    // If the popover is open or hovered, read the comment
    if (isOpen) {
      readComment()
    }
  }, [isOpen, readComment, selectedRoot])

  const handleMouseEnter = React.useCallback(() => {
    clearTimeout(closeTimerRef.current)
    setIsHovered(true)
  }, [setIsHovered])

  const handleMouseLeave = React.useCallback(() => {
    if (
      !isOpen &&
      !hasSelectionRef.current &&
      !isPointerDownOnContentRef.current
    ) {
      closeTimerRef.current = window.setTimeout(
        () => setIsHovered(false),
        closeDelay
      )
    }
  }, [closeDelay, isOpen, setIsHovered])

  // handleSelectionChange ----------------------------------------------
  React.useEffect(() => {
    if (ref.current) {
      const handlePointerUp = () => {
        isPointerDownOnContentRef.current = false

        // Delay a frame to ensure we always access the latest selection
        setTimeout(() => {
          const hasSelection = document.getSelection()?.toString() !== ''
          if (hasSelection) hasSelectionRef.current = true
        })
      }

      document.addEventListener('pointerup', handlePointerUp)
      return () => {
        document.removeEventListener('pointerup', handlePointerUp)
        hasSelectionRef.current = false
        isPointerDownOnContentRef.current = false
      }
    }
  }, [isPointerDownOnContentRef, hasSelectionRef])

  // handle click outside ----------------------------------------------
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      const isClickInsideInnerPopover =
        target.closest('[data-inner-popover="true"]') !== null ||
        event.target === document.documentElement

      if (
        isOpen &&
        !isClickInsideInnerPopover &&
        ref.current &&
        !ref.current.contains(event.target as Node) &&
        contentRef.current &&
        !contentRef.current.contains(event.target as Node)
      ) {
        clearSearchParams()
        setIsOpen(false)
        setIsHovered(false)
        setThreadPickerIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, clearSearchParams])

  // On escape key ----------------------------------------------
  React.useEffect(() => {
    if (!isOpenResolved) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false)
        setIsHovered(false)
        e.stopPropagation()
        e.preventDefault()
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpenResolved])

  // on open scroll to bottom ----------------------------------------------
  React.useEffect(() => {
    if (!isOpen) return

    const scrollable = contentRef.current?.querySelector(
      '[data-radix-scroll-area-viewport]'
    )

    if (!scrollable) return

    // kick off first scroll
    // rIC(() => {
    scrollable.scrollTop = scrollable.scrollHeight
    // })
  }, [isOpen, selectedRoot])

  // when has commentId in url, open the comment and toggle the root if not selected ----------------------------------------------
  React.useEffect(() => {
    if (searchParams.has('commentId')) {
      const commentId = searchParams.get('commentId')

      const threadRootFromSearchParams = threadRoots.find(
        (r) => r.id === commentId
      )

      if (threadRootFromSearchParams) {
        if (threadRootFromSearchParams.id !== selectedRootId) {
          if (!replicache || !user?.id || !branch) return

          replicache.mutate
            .setSelectedThreadByBlock({
              userId: user.id,
              blockId: sourceBlockId,
              branchId: branch?.id,
              rootComment: threadRootFromSearchParams,
            })
            .then(() => setIsOpen(true))
        } else {
          setIsOpen(true)
        }
      }
    }
  }, [
    branch,
    replicache,
    searchParams,
    selectedRoot,
    selectedRootId,
    sourceBlockId,
    threadRoots,
    user?.id,
  ])

  // when click on the popover on hover mode this will open the popover -----------------
  const handlePopoverClick = React.useCallback(
    (e: React.MouseEvent) => {
      // check if isnt clicking an input or button or label, etc
      if (isOpen) {
        return
      }
      const target = e.target as HTMLElement
      if (
        target &&
        target.closest?.('input, button, label, [contenteditable]')
      ) {
        return
      }

      e.preventDefault()
      setIsOpen(true)
    },
    [isOpen]
  )

  // sync internal popover state with the url and local state
  const onOpenChange = React.useCallback(
    (open: boolean) => {
      if (!open) {
        setIsOpen(false)
        setIsHovered(false)
        clearSearchParams()
      }
    },
    [clearSearchParams]
  )

  const hasRendered = useHasRendered()

  if (!hasRendered || !user) return null

  return (
    <div
      ref={ref}
      className={clsx(
        shouldShow ? '!visible !opacity-100' : 'invisible opacity-0',
        { 'translate-y-[13px]': !thread || thread.length === 0 },
        className
      )}
    >
      <Popover
        open={(isHovered && (thread?.length ?? 0) > 0) || isOpen}
        onOpenChange={onOpenChange}
      >
        <div className="absolute">
          <CommentTrigger
            ref={commentTriggerRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            thread={thread}
            roots={roots}
            onClick={async (e, selectedThread) => {
              e.preventDefault()
              if (!selectedThread && replicache && branch && lastUnreadThread) {
                await replicache.mutate.setSelectedThreadByBlock({
                  userId: user.id,
                  blockId: sourceBlockId,
                  branchId: branch?.id,
                  rootComment: lastUnreadThread.rootComment,
                })
              }
              setIsOpen((prev) => !prev)
            }}
          />
        </div>

        {/* this fake trigger ties the popover content to it becase the real trigger changes its size and causes shift */}
        <PopoverTrigger asChild>
          <span className="size-[34px] pointer-events-none" />
        </PopoverTrigger>

        <PopoverPortal forceMount>
          <PopoverContent
            // prevent closing if the focus or pointer down is on the trigger
            onInteractOutside={(e) => {
              if (!e.currentTarget) return

              if (
                commentTriggerRef.current &&
                (commentTriggerRef.current.contains(e.currentTarget as Node) ||
                  commentTriggerRef.current === e.currentTarget)
              ) {
                e.preventDefault()
              }
            }}
            onClick={handlePopoverClick}
            forceMount
            onCloseAutoFocus={(e) => e.preventDefault()}
            onOpenAutoFocus={(e) => e.preventDefault()}
            ref={contentRef}
            collisionPadding={16}
            collisionBoundary={[document.getElementById(tabContentWrapperId)]}
            align={align}
            side={side}
            sideOffset={8}
            className="bg-dark-base focus:outline-none focus-visible:outline-none flex flex-col shadow-modal !max-w-[320px] !z-tooltip radix-state-open:animate-scale-in-fade rounded-xl !duration-75 origin-[var(--radix-popover-content-transform-origin,_top_right)]"
            style={{ display: isOpenResolved ? 'block' : 'none' }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <CommentBox
              type={type}
              threadPickerToggleState={{
                isOn: threadPickerIsOpen,
                handleOn: () => setThreadPickerIsOpen(true),
                handleOff: (e) => {
                  setThreadPickerIsOpen(false)
                  setIsHovered(true)
                  if (!e || !(e instanceof KeyboardEvent)) {
                    setIsOpen(true)
                  }
                },
              }}
              thread={thread}
              block={block}
              handleOff={() => {
                setIsOpen(false)
                setIsHovered(false)
              }}
              isOpen={isOpen}
            />
          </PopoverContent>
        </PopoverPortal>
      </Popover>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/*                                   Trigger                                  */
/* -------------------------------------------------------------------------- */

const CommentTrigger = React.forwardRef<
  HTMLButtonElement,
  {
    thread: Comment[] | null | undefined
    roots: Set<RootComment>
  } & Omit<React.HTMLAttributes<HTMLButtonElement>, 'onClick'> & {
      onClick: (
        e: React.MouseEvent<HTMLButtonElement>,
        selectedThread: RootComment | undefined | null
      ) => void
    }
>(({ thread, roots, ...props }, ref) => {
  const selectedRootComment = React.useMemo(() => {
    const rootThread = thread?.find((c) => c.type === 'root')
    if (!rootThread) return null
    return rootThread as RootComment
  }, [thread])

  const getLastComment = useLastCommentGetter()
  const { data: user } = useUserData(selectedRootComment?.authorId)
  const lastUnreadThread = useLastUnreadThread(
    Array.from(roots)
      .map((rootComment) => {
        const lastComment = getLastComment(rootComment)
        if (!lastComment) return null

        return { rootComment, lastComment }
      })
      .filter(Boolean),
    { selectedRootId: selectedRootComment?.id || null }
  )

  if (selectedRootComment) {
    return (
      <button
        className={clsx(
          'rounded-full cursor-default border rounded-bl-none p-1 size-[34px] flex items-center justify-center',
          {
            'border-dark-accent bg-dark-shade-hover hover:bg-dark-shade':
              selectedRootComment.id !== lastUnreadThread?.rootComment.id,
            'border-dark-border-solid bg-dark-shade-hover hover:bg-dark-shade':
              !lastUnreadThread,
            'border-dark-accent bg-dark-accent hover:bg-dark-control-highlight hover:border-dark-control-highlight':
              selectedRootComment.id === lastUnreadThread?.rootComment.id,
          }
        )}
        ref={ref}
        {...props}
        onClick={(e) => {
          props.onClick(e, selectedRootComment)
        }}
      >
        <BaseHubAvatar
          size="small"
          id={selectedRootComment.authorId}
          name={user ? makeFullNameFromClerkUser(user) : ''}
          className="pointer-events-none"
        />
      </button>
    )
  }

  return (
    <IconButton
      ref={ref}
      className={clsx(
        'cursor-default relative',
        !!lastUnreadThread &&
          'after:absolute after:size-1.5 after:rounded-full after:bg-dark-accent after:top-1 after:right-1 after:content-[""]'
      )}
      {...props}
      onClick={(e) => {
        props.onClick(e, selectedRootComment)
      }}
    >
      <CommentIcon className="size-4 text-dark-faint" />
    </IconButton>
  )
})

/* -------------------------------------------------------------------------- */
/*                                   Content                                  */
/* -------------------------------------------------------------------------- */

const CommentBox = ({
  thread,
  block,
  type,
  className,
  handleOff,
  threadPickerToggleState,
  isOpen,
  ...props
}: {
  thread?: Comment[] | null
  block: Block | BlockChange
  type: 'block' | 'diff'
  handleOff: () => void
  threadPickerToggleState: ThreadPickerToggleState
  isOpen: boolean
} & React.HTMLAttributes<HTMLDivElement>) => {
  const user = useUserStore((s) => s.optimisticUser)
  const getNode = useWorkingTreeStore((s) => s.tree?.getNode)
  const containerRef = React.useRef<HTMLDivElement | null>(null)
  const lastCreatedCommentRef = React.useRef<{
    id: string
    height: number | undefined
  } | null>(null)
  const [editingCommentId, setEditingCommentId] = React.useState<string | null>(
    null
  )

  const repoReplicache = useRepoReplicache().data
  const repo = repoReplicache?.repo
  const replicache = repoReplicache?.replicache
  const { teamSlug, repoSlug } = useDashboardSlugs()
  const branch = useSelectedBranch().branch
  const branchOrCommit = useSelectedBranchOrCommitQuery().data
  const rootComment = thread?.[0] as RootComment | undefined
  const editorRef = React.useRef<Editor | null>(null)
  const sourceBlockId = idToSourceBlockId(block)
  const parentKey = getNode?.(block.id)?.parentKey
  const parentNode = parentKey ? getNode?.(parentKey) : undefined
  const parentBlockIdForComment =
    isBlockWithChildren(block) || !parentNode
      ? undefined
      : idToSourceBlockId(parentNode.value)
  const editorKey =
    'comment-' +
    branchOrCommit?.id +
    '-' +
    sourceBlockId +
    '-' +
    rootComment?.id +
    '-' +
    type

  const setSelectedThreadByRoot = React.useCallback(
    async (rootComment: RootComment | null) => {
      if (!replicache || !user?.id || !branch) return

      await replicache.mutate.setSelectedThreadByBlock({
        userId: user.id,
        blockId: sourceBlockId,
        branchId: branch?.id,
        rootComment,
      })
    },
    [branch, replicache, sourceBlockId, user?.id]
  )

  const handleSubmit = React.useCallback(
    async (comment: string, _id?: string) => {
      if (
        !branch?.id ||
        !user?.id ||
        !replicache ||
        !repoSlug ||
        !teamSlug ||
        !repo?.title
      ) {
        return
      }
      const currentEditorHeight = editorRef.current?.view.dom.clientHeight
      const id = _id ?? getId()

      lastCreatedCommentRef.current = {
        id,
        height: (currentEditorHeight ?? 0) - 28,
      }

      const newComment = await addCommentOrReply({
        id,
        branchId: branch?.id,
        replicache,
        userId: user.id,
        value: comment,
        blockId: sourceBlockId,
        ref: type,
        thread,
        authorUsername: user.username!,
        branchName: branch.name,
        repoSlug,
        teamSlug,
        parentBlockId: parentBlockIdForComment,
        isDefaultBranch: branch.isDefault,
        repoName: repo?.title,
      })
      if (newComment.type === 'root') {
        setSelectedThreadByRoot(newComment)
      }
      setTimeout(() => {
        editorRef.current?.commands.focus('end')
      }, 300)
    },
    [
      branch?.id,
      branch?.name,
      branch?.isDefault,
      user?.id,
      user?.username,
      replicache,
      repoSlug,
      teamSlug,
      repo?.title,
      sourceBlockId,
      type,
      thread,
      parentBlockIdForComment,
      setSelectedThreadByRoot,
    ]
  )

  React.useEffect(() => {
    if (!isOpen) {
      setEditingCommentId(null)
      return
    }
    // auto focus the editor
    const t = setTimeout(() => {
      editorRef.current?.commands.focus('end')
    }, 20)
    return () => clearTimeout(t)
  }, [isOpen, rootComment?.id])

  if (!user) return null

  // Empty state
  if (!thread) {
    return isOpen ? (
      <>
        <CommentBoxHeader
          {...props}
          className="rounded-t-lg"
          block={block}
          threadPickerToggleState={threadPickerToggleState}
          actions={[
            ...(isOpen
              ? [
                  {
                    icon: <Cross1Icon className="size-3.5" />,
                    label: 'Close',
                    onClick: handleOff,
                  },
                ]
              : []),
          ]}
        />

        <CommentForm
          editorKey={editorKey}
          placeholder="Leave a comment..."
          className="min-w-[320px] rounded-b-lg overflow-clip border-x border-b border-dark-border-solid"
          editorClassName="!rounded-none !bg-dark-base-dark"
          onSubmit={handleSubmit}
          autofocus
          size="none"
          onUpdate={({ editor }) => (editorRef.current = editor)}
          alwaysShowPlaceholder
          onCreate={({ editor }) => {
            editorRef.current = editor
            editor.commands.focus('end')
          }}
          onBeforeCreate={({ editor }) => (editorRef.current = editor)}
        />
      </>
    ) : null
  }

  const toggleResolveAction = !!rootComment?.resolvedBy
    ? {
        icon: (
          <span className="bg-dark-control-base size-[15px] rounded-full text-dark-bg ml-auto flex items-center justify-center">
            <CheckIcon className="size-2.5" />
          </span>
        ),
        label: 'Reopen conversation',
        onClick: async () => {
          if (!rootComment || !user || !replicache) return
          await replicache.mutate.openComment({ id: rootComment.id })
        },
      }
    : {
        icon: <CheckCircledIcon className="size-3.5" />,
        label: 'Resolve conversation',
        onClick: async () => {
          if (!rootComment || !user || !replicache) return
          await replicache.mutate.resolveComment({
            id: rootComment.id,
            userId: user.id,
          })

          if (branch) {
            // takes the user to the new thread when resolved
            await replicache.mutate.setSelectedThreadByBlock({
              userId: user.id,
              blockId: sourceBlockId,
              branchId: branch?.id,
              rootComment: null,
            })
          }

          handleOff()
        },
      }

  // Main
  return (
    <div
      {...props}
      className={clsx(
        'w-[320px] rounded-xl flex-1 h-full flex flex-col bg-dark-base',
        className
      )}
    >
      {/* Header */}
      <CommentBoxHeader
        className="rounded-t-xl"
        block={block}
        threadPickerToggleState={threadPickerToggleState}
        actions={[
          toggleResolveAction,
          ...(isOpen
            ? [
                {
                  icon: <Cross1Icon className="size-3.5" />,
                  label: 'Close',
                  onClick: handleOff,
                },
              ]
            : []),
        ]}
      />

      <ScrollArea
        viewportClassName="max-h-[clamp(350px,70vh,800px)] border-x border-b border-dark-border-solid rounded-b-xl"
        ref={containerRef}
        variant="subtle"
      >
        {/* Main (thread, reactions, edit) */}
        <CommentThreadBox threadId={rootComment!.id}>
          {/* eslint-disable react-compiler/react-compiler */}
          {thread?.map((comment, index) => {
            return (
              <Message
                key={comment.id}
                rootComment={rootComment!}
                comment={comment}
                isEditingComment={editingCommentId === comment.id}
                setEditingCommentId={setEditingCommentId}
                updateComment={async (id, value) => {
                  if (!replicache) return
                  await replicache.mutate.updateComment({
                    id,
                    value,
                    lastEditedAt: new Date().toISOString(),
                  })
                }}
                className={clsx(!isOpen && index > 0 && 'hidden')}
                enabledActions={isOpen}
                enableStreaming={isOpen}
                isCommentCreatedJustNow={
                  lastCreatedCommentRef.current?.id === comment.id
                    ? lastCreatedCommentRef.current
                    : false
                }
              />
            )
          })}
        </CommentThreadBox>
        {/* New comment form (only show when fully open) */}
        <div style={{ display: isOpen ? 'block' : 'none' }}>
          {isOpen && (
            <CommentForm
              editorClassName="light:bg-dark-shade"
              editorKey={
                'comment-' +
                branchOrCommit?.id +
                '-' +
                sourceBlockId +
                '-' +
                rootComment?.id +
                '-' +
                +type
              }
              placeholder={
                !!rootComment?.resolvedBy
                  ? 'Reply to reopen thread...'
                  : 'Reply'
              }
              className="min-h-[52px]"
              onDestroy={() => {
                setEditingCommentId(null)
              }}
              onUpdate={({ editor }) => {
                editorRef.current = editor
                const scrollable = containerRef.current?.querySelector(
                  '[data-radix-scroll-area-viewport]'
                )
                if (scrollable) {
                  scrollable.scrollTo({
                    top: scrollable.scrollHeight,
                    behavior: 'instant',
                  })
                }
              }}
              onCreate={({ editor }) => {
                editorRef.current = editor
              }}
              onSubmit={handleSubmit}
            />
          )}
        </div>
      </ScrollArea>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/*                               Single Message                               */
/* -------------------------------------------------------------------------- */

export const MessageContext = React.createContext<{
  comment: Comment
  rootComment: RootComment
} | null>(null)

export const useMessageContext = () => {
  return React.useContext(MessageContext)
}

export const Message = ({
  rootComment,
  comment,
  isEditingComment,
  setEditingCommentId,
  updateComment,
  className,
  enabledActions = true,
  isCommentCreatedJustNow,
  isHovered,
  enableStreaming = false,
  ...props
}: {
  rootComment: RootComment
  comment: Comment
  isEditingComment: boolean
  setEditingCommentId: (id: string | null) => void
  updateComment: (id: string, value: string) => Promise<void>
  className?: string
  enabledActions?: boolean
  isCommentCreatedJustNow?: false | { id: string; height: number | undefined }
  isHovered?: boolean
  enableStreaming?: boolean
} & React.HTMLAttributes<HTMLDivElement>) => {
  const commentEditorRef = React.useRef<Editor | null>(null)
  const [heightPostUpdate, setHeightPostUpdate] = React.useState<number>()

  React.useEffect(() => {
    if (isEditingComment) {
      const t = setTimeout(() => {
        commentEditorRef.current?.commands.focus('end')
      }, 100)
      return () => clearTimeout(t)
    }
  }, [isEditingComment])

  const handleSubmit = React.useCallback(
    async (value: string) => {
      const newInitialHeight = commentEditorRef.current?.view.dom.clientHeight
      await updateComment(comment.id, value)
      setHeightPostUpdate(newInitialHeight)
      setEditingCommentId(null)
    },
    [updateComment, comment.id, setEditingCommentId]
  )

  const { content, isStreaming } = useCommentContent({
    comment,
    enableStreaming,
  })

  const editorKey = `comment-${comment.id}/$${
    isEditingComment ? 'editing' : 'viewing'
  }-${comment.lastEditedAt}`

  return (
    <MessageContext.Provider value={{ comment, rootComment }}>
      <CommentWrapper
        isHovered={isHovered}
        enabledActions={enabledActions}
        comment={comment}
        className={className}
        onEdit={() => {
          setEditingCommentId(comment.id)
          if (commentEditorRef.current) {
            setHeightPostUpdate(
              commentEditorRef.current?.view.dom.clientHeight + 23
            )
          }
        }}
        {...props}
      >
        <FeedbackCommentEditor
          editorKey={editorKey}
          className={clsx(
            isEditingComment &&
              'bg-dark-shade-hover rounded-lg p-4 pt-3 min-h-6 !max-h-full mt-1 mr-1 !gap-y-4'
          )}
          initialHeight={
            isCommentCreatedJustNow
              ? isCommentCreatedJustNow.height
              : heightPostUpdate
          }
          content={content}
          editable={isEditingComment}
          onCreate={({ editor }) => {
            commentEditorRef.current = editor
          }}
          onUpdate={({ editor }) => {
            commentEditorRef.current = editor
          }}
          onDestroy={() => {
            commentEditorRef.current = null
          }}
          onCancel={() => {
            setEditingCommentId(null)
            const newInitialHeight =
              commentEditorRef.current?.view.dom.clientHeight
            setHeightPostUpdate(newInitialHeight)
          }}
          slotAfter={
            isEditingComment && (
              <CommentFormFooter
                submitIcon={<CheckIcon className="size-3.5" />}
                handleSubmit={handleSubmit}
                handleCancel={() => {
                  setEditingCommentId(null)
                  const newInitialHeight =
                    commentEditorRef.current?.view.dom.clientHeight
                  setHeightPostUpdate(newInitialHeight)
                }}
                commentIsEmpty={undefined}
                editorKey={editorKey}
              />
            )
          }
        />
        {isStreaming && (
          <div className="flex items-end gap-2 text-xs text-dark-faint mt-2">
            <span className="animate-pulse duration-1400">Streaming in...</span>
          </div>
        )}
        <CommentReactions
          isEditingComment={isEditingComment}
          comment={comment}
        />
      </CommentWrapper>
    </MessageContext.Provider>
  )
}

export const useCommentContent = ({
  comment,
  enableStreaming,
}: {
  comment: Comment
  enableStreaming: boolean
}) => {
  const {
    messages: streamMessages,
    sendMessage,
    isConnected,
  } = useWebSocketConnection(
    enableStreaming ? (comment.streamURL ?? undefined) : undefined
  )

  React.useEffect(() => {
    if (isConnected) {
      sendMessage('GET_INITIAL_CHUNKS')
    }
  }, [isConnected, sendMessage])

  const isStreamComplete = streamMessages.some(
    (m) => m.type === 'received' && m.data === 'COMPLETE'
  )
  const isStreaming = !comment.value && comment.streamURL && !isStreamComplete

  const content = React.useMemo(() => {
    let _content: string | JSONContent = ''
    if (comment.value) {
      _content = safeParseCommentValue(comment.value)
    } else if (comment.streamURL) {
      const serverMessages = streamMessages.filter(
        (m) =>
          m.type === 'received' &&
          m.data !== 'END_OF_INITIAL_CHUNKS' &&
          m.data !== 'COMPLETE'
      )
      if (serverMessages.length > 0) {
        const joined = serverMessages[serverMessages.length - 1]?.data
        const result = safeParseCommentValue(joined)
        _content = result
      }
    }

    return _content
  }, [comment.value, comment.streamURL, streamMessages])

  return { content, isStreaming }
}

type ThreadPickerToggleState = {
  isOn: boolean
  handleOn: () => void
  handleOff: (e?: KeyboardEvent | MouseEvent) => void
}

export const CommentBoxHeader = ({
  actions,
  threadPickerToggleState,
  block,
  className,
  ...props
}: {
  actions: { icon: React.ReactNode; label: string; onClick: () => void }[]
  threadPickerToggleState: ThreadPickerToggleState
  block: Block | BlockChange
} & React.HTMLAttributes<HTMLDivElement>) => {
  const branch = useSelectedBranch().branch
  const sourceBlockId = idToSourceBlockId(block)
  const replicache = useRepoReplicache().data?.replicache
  const userId = useUserStore((s) => s.optimisticUser?.id)

  const threadRoots = useCommentsStore((s) => {
    const roots = s.rootsByBlockId.get(sourceBlockId)
    const threadRoots: Array<RootComment> = []

    for (const rId of roots ?? []) {
      const comment = s.commentsMap.get(rId)
      if (!comment) continue
      if (comment.type !== 'root') continue
      if (comment.blockId !== sourceBlockId) continue

      const isInAPlaygroundOfBranch =
        !!branch?.playgroundId && branch?.sourceBranchId === comment.branchId
      if (comment.branchId !== branch?.id && !isInAPlaygroundOfBranch) continue
      threadRoots.push(comment)
    }

    // sort by createdAt
    return [...threadRoots].sort((a, b) => {
      return a.createdAt >= b.createdAt ? 1 : -1
    })
  })

  // Clear search params ----------------------------------------------
  const clearSearchParams = React.useCallback(() => {
    const searchParams = new URLSearchParams(window.location.search)
    searchParams.delete('commentId')
    window.history.replaceState(
      {},
      '',
      `${window.location.pathname}?${searchParams}`
    )
  }, [])

  const selectedRootId = useUserPreferences((s) => {
    const threadsOfThisBlock = s.selectedThreads[sourceBlockId]

    if (!threadsOfThisBlock?.length) return null

    return (
      threadsOfThisBlock.find((root) => {
        return root.blockId === sourceBlockId && root.branchId === branch?.id
      })?.id ?? null
    )
  })

  const selectedRoot = useCommentsStore((s) => {
    const roots = s.rootsByBlockId.get(sourceBlockId)
    const selectedRoot = roots?.has(selectedRootId ?? '')
      ? s.commentsMap.get(selectedRootId!)
      : null
    invariant(
      !selectedRoot || selectedRoot.type === 'root',
      'Expected comment to be root'
    )
    return (selectedRoot as RootComment | null | undefined) ?? null
  })

  const setSelectedThreadByRoot = async (rootComment: RootComment | null) => {
    if (!replicache || !userId || !branch) return

    await replicache.mutate.setSelectedThreadByBlock({
      userId,
      blockId: sourceBlockId,
      branchId: branch?.id,
      rootComment,
    })
  }

  return (
    <div
      {...props}
      className={clsx(
        'flex gap-1 justify-between border border-dark-border-solid bg-dark items-center bg-dark-base-dark light:bg-dark-shade sticky top-0 z-10 pr-3',
        className
      )}
    >
      <Select
        value={selectedRoot?.id ?? '__new'}
        open={threadPickerToggleState.isOn}
        onValueChange={(value) => {
          clearSearchParams()
          // if new thread, remove the selected root by using null
          const threadCandidate = value.startsWith('__')
            ? null
            : (threadRoots.find((r) => r.id === value) ?? null)
          setSelectedThreadByRoot(threadCandidate)
        }}
        onOpenChange={(o) => {
          if (o) {
            threadPickerToggleState.handleOn()
          } else {
            threadPickerToggleState.handleOff()
          }
        }}
      >
        <SelectTrigger
          unstyled
          className="text-sm text-dark-dim font-medium inline-flex cursor-default items-center w-full h-10 pl-3 focus-visible:outline outline-offset-0 focus-visible:outline-dark-control-base rounded-tl-xl [&>span]:overflow-hidden [&>span]:text-ellipsis whitespace-nowrap overflow-hidden"
          caretClassName="!size-[18px] mr-0.5"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent
          data-inner-popover
          className="max-w-[300px]"
          onEscapeKeyDown={(e) => {
            e.preventDefault()
            e.stopPropagation()
            threadPickerToggleState.handleOff(e)
          }}
        >
          {Boolean(threadRoots.length) && (
            <>
              <SelectGroup>
                {threadRoots.map((root) => (
                  <SelectThreadItem key={root.id} root={root} />
                ))}
              </SelectGroup>
              <SelectSeparator />
            </>
          )}
          <SelectGroup>
            <SelectItem
              value="__new"
              indicator={
                <span className="absolute left-1.5 flex h-3 w-3 items-center justify-center">
                  <PlusIcon className="size-[15px]" />
                </span>
              }
            >
              New thread
            </SelectItem>
          </SelectGroup>
        </SelectContent>
      </Select>

      {/* comment actions */}
      <div className="flex gap-1 text-dark-faint">
        <ThreadActions actions={actions} />
      </div>
    </div>
  )
}

const SelectThreadItem = ({ root }: { root: RootComment }) => {
  const thread = useThread(root)
  const hasRead = useHasReadThread(root, thread)

  return (
    <SelectItem
      key={root.id}
      value={root.id}
      className="overflow-hidden [&>span]:whitespace-nowrap [&>span]:text-ellipsis [&>span]:overflow-hidden"
      afterText={
        !!root.resolvedBy ? (
          <span className="bg-dark-faint size-[15px] rounded-full text-dark-bg mr-1 flex items-center justify-center ml-2">
            <CheckIcon className="size-3" />
          </span>
        ) : (
          !hasRead && (
            <span className="size-2 rounded-full bg-dark-accent mr-2 ml-2.5" />
          )
        )
      }
    >
      <span>{root.label ?? 'New thread'}</span>
    </SelectItem>
  )
}

const useHasReadThread = (
  rootComment: Comment | null | undefined,
  thread: Comment[] | null | undefined
) => {
  const lastViewedAt = useUserPreferences(
    (s) => s.commentsRead[rootComment?.id ?? '']
  )

  // check if the current user has viewed the last comment
  const currentUserId = useUserStore((s) => s.optimisticUser?.id)
  const lastComment = thread?.[thread.length - 1]
  return (
    currentUserId === lastComment?.authorId ||
    (lastViewedAt &&
      new Date(lastComment?.createdAt ?? '') < new Date(lastViewedAt))
  )
}

type ThreadBounds = { rootComment: RootComment; lastComment: Comment }

const useLastUnreadThread = (
  threads: Array<ThreadBounds>,
  { selectedRootId = null }: { selectedRootId: string | null | undefined }
): ThreadBounds | undefined => {
  const commentsRead = useUserPreferences((s) => s.commentsRead)
  const currentUserId = useUserStore((s) => s.optimisticUser?.id)
  const branch = useSelectedBranch().branch

  return React.useMemo(() => {
    for (let i = threads.length - 1; i >= 0; i--) {
      const thread = threads[i]
      if (!thread) continue
      const { rootComment, lastComment } = thread
      if (rootComment.resolvedBy) continue
      if (selectedRootId === rootComment.id) continue
      if (!lastComment) continue
      if (!branch || rootComment.branchId !== branch.id) continue
      if (currentUserId === lastComment.authorId) continue
      const lastReadAt = commentsRead[rootComment.id]
      if (!lastReadAt || lastComment.createdAt > lastReadAt) {
        return thread
      }
    }

    return undefined
  }, [threads, selectedRootId, branch, currentUserId, commentsRead])
}

export const addCommentOrReply = async ({
  value,
  parentCommentId,
  branchId,
  userId,
  replicache,
  thread,
  blockId = null,
  ref,
  id,
  branchName,
  repoSlug,
  teamSlug,
  authorUsername,
  parentBlockId,
  isDefaultBranch,
  repoName,
}: {
  id?: string
  thread?: Comment[] | null
  blockId?: string | null
  deletedAt?: string
  ref?: 'diff' | 'block' | 'agent'
  value: string
  parentCommentId?: string
  branchId: string
  userId: string
  replicache: Replicache<typeof repoSpaceMutators>
  branchName: string
  repoSlug: string
  teamSlug: string
  authorUsername: string
  parentBlockId?: string | null
  isDefaultBranch: boolean
  repoName: string
} & Omit<
  RootComment,
  | 'id'
  | 'type'
  | 'status'
  | 'lastEditedAt'
  | 'replyId'
  | 'authorId'
  | 'createdAt'
  | 'reactions'
  | 'branchId'
  | 'deletedAt'
>) => {
  invariant(branchId, 'branch must be defined')
  invariant(userId, 'user must be defined')
  invariant(replicache, 'replicache must be defined')

  let newComment: Comment
  if (!thread || thread.length === 0) {
    // add root comment
    const result = await replicache.mutate.addComment({
      comment: {
        id: id ?? getId(),
        value,
        type: 'root',
        authorId: userId,
        createdAt: new Date().toISOString(),
        reactions: {},
        branchId,
        blockId,
        replyId: null,
        status: 'open',
        lastEditedAt: null,
        ref,
      },
      blockId,
      blockParentId: parentBlockId ?? null,
      branchSlug: branchName,
      repoSlug,
      teamSlug,
      authorUsername,
      isDefaultBranch,
      repoName,
    })
    newComment = result.comment
  } else {
    // add reply
    const parent = thread![thread!.length - 1]
    invariant(parent, 'parent should be exist at this point')

    const result = await replicache.mutate.addComment({
      comment: {
        id: id ?? getId(),
        value,
        type: 'reply',
        authorId: userId,
        createdAt: new Date().toISOString(),
        reactions: {},
        parentId: parentCommentId ?? parent!.id,
        replyId: null,
        rootId: thread?.find((c) => c.type === 'root')!.id,
        lastEditedAt: null,
      },
      blockId,
      blockParentId: parentBlockId ?? null,
      branchSlug: branchName,
      authorUsername,
      repoSlug,
      teamSlug,
      isDefaultBranch,
      repoName,
    })
    newComment = result.comment
  }

  return newComment
}

export default CommentsPopover
